import classNames from "classnames";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as MessageNotificationIcon } from "assets/images/common/message-notice.svg";
import { ReactComponent as MessageIcon } from "assets/images/common/message.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";
import { ReactComponent as RestartIcon } from "assets/images/common/restart.svg";
import { ReactComponent as ShieldIcon } from "assets/images/common/shield.svg";
import { ReactComponent as UsersIcon } from "assets/images/common/users.svg";

import useDebounce from "hooks/useDebounce";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import InfoCard from "components/InfoCard";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";
import TableRowActions from "components/Table/TableRowActions";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  addCompanyUserRequest,
  resendCompanyUserInvitationRequest,
  resetIsResetted,
  resetSuccessfullyAddedCompany,
  resetSuccessfullyUpdatedCompany,
  updateCompanyUserRequest,
} from "store/slices/companiesSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";
import { changePage, getUsersListRequest, getUserStatisticsRequest, resetUserList } from "store/slices/usersSlice";

import PageTemplate from "modules/common/PageTemplate";

import { ModalType, UserRole } from "entities/common";
import { AddUserRequest, UserFormValues, UserStatuses } from "entities/companies";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import { CompaniesApi } from "services/http";

import styles from "./style.module.scss";

const UsersPage = () => {
  const callbackKey = useId();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { successfullyRemovedId, items, meta, statistics, isStatisticsLoading } = useAppSelector(
    (state) => state.users,
  );
  const { successfullyAddedId, successfullyUpdatedId, isResetted } = useAppSelector((state) => state.companies);
  const isAdmin = user?.role === UserRole.ADMINISTRATOR;
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<UserStatuses | "all">("all");
  const [selectedCompany, setSelectedCompany] = useState<string | null>("all");
  const debouncedSearch = useDebounce(search, 500);

  const handleSaveUser = useCallback(
    (values?: unknown) => {
      const data = values as UserFormValues;

      const body: AddUserRequest = {
        full_name: data.name,
        email: data.email,
        phone: data.phone,
        position: data.position,
      };

      dispatch(addCompanyUserRequest({ companyId: Number(data.company), body }));
    },
    [dispatch],
  );

  const handleUpdateUser = useCallback(
    (id: number, companyId: number) => (values?: unknown) => {
      const data = values as UserFormValues;

      const body: AddUserRequest = {
        full_name: data.name,
        email: data.email,
        phone: data.phone,
        position: data.position,
      };

      dispatch(updateCompanyUserRequest({ companyId: Number(companyId), userId: id, body }));
    },
    [dispatch],
  );

  const handleGoToCompanies = useCallback(() => {
    navigate(PAGES.COMPANY_LIST);
  }, [navigate]);

  const handleCreateUser = useCallback(() => {
    setModalCallbacks(callbackKey, { onSave: handleSaveUser });

    dispatch(
      showModal({ modalType: ModalType.USER, modalProps: { type: "add", withCompanySelect: isAdmin, callbackKey } }),
    );
  }, [callbackKey, dispatch, handleSaveUser, isAdmin]);

  const handleEdit = useCallback(
    (row: {
      companyId: number;
      id: number;
      name: string;
      email: string;
      phone: string;
      position: string | undefined;
      company: string;
    }) =>
      (id: number) => {
        setModalCallbacks(callbackKey, { onSave: handleUpdateUser(id, row.companyId) });

        dispatch(
          showModal({
            modalType: ModalType.USER,
            modalProps: {
              id,
              type: "edit",
              withCompanySelect: false,
              callbackKey,
              data: {
                name: row.name,
                email: row.email,
                phone: row.phone,
                position: row.position,
                company: row.company,
              },
            },
          }),
        );
      },
    [callbackKey, dispatch, handleUpdateUser],
  );

  const handleRemove = useCallback(
    (portfolioCounts: number, name: string, companyId: number) => (id: number) => {
      dispatch(
        showModal({
          modalType: ModalType.REMOVE_USER,
          modalProps: { id, companyId, portfolioCounts, name, isGlobalRemove: true },
        }),
      );
    },
    [dispatch, isAdmin],
  );

  const handleRestart = useCallback(
    (row: { companyId: number; id: number }) => {
      dispatch(resendCompanyUserInvitationRequest({ companyId: Number(row.companyId), userId: row.id }));
    },
    [dispatch],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    dispatch(changePage(1));
  };

  const handlePageChange = (page: number) => {
    dispatch(changePage(page));
  };

  const handleStatusChange = (value: string) => {
    setStatus(value as UserStatuses | "all");
    dispatch(changePage(1));
  };

  const handleCompanyChange = (value: string) => {
    setSelectedCompany(value);
    dispatch(changePage(1));
  };

  const handleCompanySearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      const hasMore = page * 50 < data?.total;
      const options = [
        { value: "all", label: "All companies" },
        ...data?.items?.map((company) => ({ value: String(company?.id), label: company?.name })),
      ];

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const cards = useMemo(() => {
    return [
      {
        title: "Total users",
        value: statistics?.total?.toString() || "-",
        icon: <UsersIcon />,
        status: "blue",
      },
      {
        title: "Active",
        value: statistics?.active?.toString() || "-",
        icon: <ShieldIcon />,
        status: "green",
      },
      {
        title: "Pending invitations",
        value: statistics?.pending?.toString() || "-",
        icon: <MessageIcon />,
        status: "orange",
      },
      {
        title: "Invitation expired",
        value: statistics?.invitation_expired?.toString() || "-",
        icon: <MessageNotificationIcon />,
        status: "red",
      },
    ];
  }, [statistics?.active, statistics?.invitation_expired, statistics?.pending, statistics?.total]);

  const tableHeaders = useMemo(
    () => [
      { label: "User", key: "user" },
      { label: "Contact", key: "contact" },
      ...(isAdmin ? [{ label: "Company", key: "company" }] : []),
      { label: "Status", key: "status" },
      { label: "Actions", key: "actions" },
    ],
    [isAdmin],
  );

  const users = useMemo(() => {
    return items.map((user) => ({
      id: user.id,
      name: user.full_name,
      email: user.email,
      phone: user.phone,
      status: user.status,
      position: user.position || undefined,
      company: user.company_name || "-",
      companyId: user.company_id,
      isOwner: user.is_owner,
      portfolioCounts: user.managed_portfolios_count,
    }));
  }, [items]);

  const tableData = useMemo(
    () =>
      users.map((row) => {
        return {
          id: row.id,
          user: (
            <div className={styles.user}>
              <span className={styles.userName}>{row.name}</span>
              <span className={styles.userPosition}>
                {row.position}
                {row.isOwner && row.position ? " / " : ""}
                {row.isOwner ? <span className={styles.userOwner}>Owner</span> : ""}
              </span>
            </div>
          ),
          contact: (
            <div className={styles.contact}>
              <span className={styles.contactEmail}>{row.email}</span>
              <span className={styles.contactPhone}>{row.phone}</span>
            </div>
          ),
          ...(isAdmin && {
            company: (
              <div className={styles.company}>
                <span>{row.company}</span>
              </div>
            ),
          }),
          status: (
            <span className={classNames(styles["status-block"])}>
              <span className={classNames(styles.status, styles[row.status])}>
                {row.status === UserStatuses.ACTIVE
                  ? "Active"
                  : row.status === UserStatuses.PENDING
                    ? "Pending"
                    : row.status === UserStatuses.INVITATION_EXPIRED
                      ? "Invitation expired"
                      : ""}
              </span>

              {row.status === UserStatuses.INVITATION_EXPIRED && (
                <Button variant="link" className={styles["status-button"]} onClick={() => handleRestart(row)}>
                  <RestartIcon />
                </Button>
              )}
            </span>
          ),
          actions: (
            <TableRowActions
              id={row.id}
              flags={{ view: false, edit: true, remove: true }}
              onEdit={handleEdit(row)}
              onRemove={handleRemove(row.portfolioCounts, row.name, row.companyId)}
              isDisabledRemove={row.isOwner}
              disabledRemoveMessage={row.isOwner ? "The owner cannot be deleted" : undefined}
            />
          ),
        };
      }),
    [handleEdit, handleRemove, handleRestart, isAdmin, users],
  );

  // get user statistics
  useEffect(() => {
    dispatch(getUserStatisticsRequest());
  }, [dispatch]);

  // get users list
  useEffect(() => {
    if (isAdmin || user?.company_id) {
      dispatch(
        getUsersListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: debouncedSearch.trim(),
            page: meta.page || 1,
            [QUERY_PARAMS.STATUS]: status !== "all" ? status : undefined,
            ...(!isAdmin && user?.company_id && { [QUERY_PARAMS.COMPANY_ID]: user?.company_id }),
            ...(isAdmin && selectedCompany !== "all" && { [QUERY_PARAMS.COMPANY_ID]: selectedCompany }),
          }),
        ),
      );
    }
  }, [
    dispatch,
    debouncedSearch,
    meta.page,
    isAdmin,
    successfullyAddedId,
    successfullyUpdatedId,
    successfullyRemovedId,
    isResetted,
    user?.company_id,
    status,
    selectedCompany,
  ]);

  // successfully added user
  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(resetSuccessfullyAddedCompany());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-added-user",
          message: "The user was successfully added.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyAddedId, dispatch]);

  // successfully updated user
  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(resetSuccessfullyUpdatedCompany());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-updated-user",
          message: "The user was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch]);

  useEffect(() => {
    if (isResetted) {
      dispatch(resetIsResetted());
    }
  }, [isResetted, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetUserList());
    };
  }, [dispatch]);

  return (
    <PageTemplate
      title="Users"
      description={"Manage user access"}
      actions={
        <Button className={styles["header-create"]} variant="primary" onClick={handleCreateUser}>
          <span className={styles["header-create-text"]}>Invite user</span>
          <span className={styles["header-create-icon"]}>
            <PlusIcon />
          </span>
        </Button>
      }
      className={styles.page}
    >
      <>
        <div className={styles.detailCards}>
          {cards.map((card) => (
            <InfoCard
              key={card.title}
              className={styles.detailCard}
              color={card.status as "orange"}
              size="basic"
              variant="primary"
              isLoading={isStatisticsLoading}
            >
              <div className={styles.detailCardHeader}>
                <span className={styles.detailCardTitle}>{card.title}</span>
                <span className={classNames(styles.detailCardIcon, styles[card.status])}>{card.icon}</span>
              </div>

              <p className={styles.detailCardValue}>{card.value}</p>
            </InfoCard>
          ))}
        </div>

        <Card className={styles.mainCard}>
          <div className={styles["action-container"]}>
            <SearchInput
              name="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search users..."
              className={styles["search-input"]}
            />

            {isAdmin && (
              <AsyncSearchableSelect
                placeholder="Select company"
                onSearch={handleCompanySearch}
                onChange={handleCompanyChange}
                value={selectedCompany || undefined}
                className={styles["status-select"]}
              />
            )}

            <Select
              options={[
                { label: "All Statuses", value: "all" },
                { label: "Active", value: UserStatuses.ACTIVE },
                { label: "Pending", value: UserStatuses.PENDING },
                { label: "Invitation Expired", value: UserStatuses.INVITATION_EXPIRED },
              ]}
              onChange={handleStatusChange}
              value={status}
              className={styles["status-select"]}
            />
          </div>

          {tableData?.length === 0 && (
            <div className={styles["not-found"]}>
              <NotFoundIcon />

              <p>{!statistics?.total ? "No users yet. Add first\n company to invite first user" : "No users found"}</p>

              {!statistics?.total && isAdmin && (
                <Button variant="secondary" onClick={handleGoToCompanies}>
                  Add first company
                </Button>
              )}
            </div>
          )}

          {!!tableData && tableData.length > 0 && (
            <div className={styles["table-scroll"]}>
              <Table
                headers={tableHeaders}
                data={tableData}
                pagination={{
                  total: meta.total_pages,
                  page: meta.page,
                  onPageChange: handlePageChange,
                }}
                className={styles.table}
              />
            </div>
          )}
        </Card>
      </>
    </PageTemplate>
  );
};

export default UsersPage;
